import { useCallback } from "react"
import { enqueueSnackbar } from "notistack"
import { useQueryClient } from "@tanstack/react-query"

import { useApiMutation } from "../../../../../core/api/useApiMutation"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { FilteredStatuses } from "./meetingServiceTypes"
import { MerchantMeetingServiceOrdersQuery, undoBatchStatusUpdate } from "./meetingServiceApi"
import { STATUS_MAP } from "./constants"
import UndoSnackBar, { UNDO_TIMEOUT } from "./components/UndoSnackbar"

export type ShowStatusUndoParams = Readonly<{
    status: FilteredStatuses
    orderIds: number[]
    orderCount: number
    onUndone?: () => void
    onSettled?: () => void
}>

export function useMeetingServiceStatusUndo() {
    const { l10n } = useAppLocalization()
    const queryClient = useQueryClient()
    const { mutate: undoBatchUpdate } = useApiMutation(undoBatchStatusUpdate, {
        async onSuccess(_params, _data, context) {
            await context.queryClient.invalidateQueries({
                queryKey: [MerchantMeetingServiceOrdersQuery.MEETING_SERVICE],
            })
        },
    })

    return useCallback(
        ({ status, orderIds, orderCount, onUndone, onSettled }: ShowStatusUndoParams) => {
            const { stateTranslationId } = STATUS_MAP[status]
            const translation = `${l10n.getString("meeting-service-snackbar-undo-message", {
                amount: orderCount,
            })} ${l10n.getString(stateTranslationId)}`

            enqueueSnackbar(translation, {
                content: (key, message) => (
                    <UndoSnackBar
                        undoLabel={l10n.getString("meeting-service-snackbar-undo")}
                        id={key}
                        message={message}
                        onUndo={() => {
                            undoBatchUpdate({ updateDeliveryStatus: { undo: true, orderIds } })
                            onUndone?.()
                        }}
                    />
                ),
                onClose: (_, reason) => {
                    if (reason === "timeout") {
                        queryClient.invalidateQueries({
                            queryKey: [MerchantMeetingServiceOrdersQuery.MEETING_SERVICE],
                        })
                        onSettled?.()
                    }
                },
                autoHideDuration: UNDO_TIMEOUT,
                anchorOrigin: { vertical: "top", horizontal: "center" },
            })
        },
        [l10n, queryClient, undoBatchUpdate],
    )
}
